import React, {useState} from 'react';
import {Badge, Dropdown, DropdownItem, DropdownMenu, DropdownToggle} from 'reactstrap';
import {clearEntraIdAuthMode} from '../../../auth/authMode';
import {getEntraIdClient} from '../../../auth/entraid/EntraIdClient';
import Resource from '../../../core/serverresource';
import EntraIdCountdownInfo from '../../feedback/entraId/EntraIdCountdownInfo';
import type {UserInfoProps} from '../UserInfo';

const getInitials = (fullName?: string | null): string => {
    const parts = fullName?.trim().split(/\s+/).filter(Boolean) ?? [];
    return parts.length > 1
        ? (parts[0][0] + parts[parts.length - 1][0]).toLocaleUpperCase()
        : (parts[0]?.slice(0, 2) ?? '?').toLocaleUpperCase();
};

export const EntraIdUserInfo = ({fullName, onToggleDebug}: UserInfoProps): JSX.Element => {
    const [isOpen, setOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const [logoutFailed, setLogoutFailed] = useState(false);
    const expiryDescription = 'Platnost aktuálního přístupového tokenu Entra ID; nejde o konec přihlášení a token se může obnovit.';
    const showTokenExpiry = Resource.getEnv() !== 'PROD';

    const logout = async (): Promise<void> => {
        if (loggingOut) {
            return;
        }
        setLoggingOut(true);
        setLogoutFailed(false);
        try {
            clearEntraIdAuthMode({
                storage: window.sessionStorage,
                url: new URL(window.location.href),
                history: window.history,
            });
            await getEntraIdClient().logout();
        } catch (error) {
            console.error('MSAL logout failed:', error);
            setLogoutFailed(true);
        } finally {
            setLoggingOut(false);
        }
    };

    return (
        <Dropdown isOpen={isOpen} toggle={() => setOpen(open => !open)} className="user-info" inNavbar>
            <DropdownToggle
                id="entra-user-menu-toggle"
                color="link"
                className="user-info-avatar-toggle p-0"
                aria-label={`Uživatelské menu: ${fullName?.trim() || 'Entra ID'}`}
            >
                <span className="user-info-avatar" aria-hidden="true">{getInitials(fullName)}</span>
            </DropdownToggle>
            <DropdownMenu right className="user-info-menu shadow" aria-labelledby="entra-user-menu-toggle">
                <div className="px-3 py-2 font-weight-bold">
                    <span>{fullName?.trim() || 'Uživatel'}</span>
                    <Badge color="primary" className="ml-2">Entra ID</Badge>
                </div>
                {showTokenExpiry && (
                    <div className="px-3 py-2 d-flex align-items-center justify-content-between text-nowrap">
                        <span title={expiryDescription} aria-label={expiryDescription}>Platnost</span>
                        {isOpen && <EntraIdCountdownInfo compact className="ml-2"/>}
                    </div>
                )}
                <DropdownItem divider/>
                <DropdownItem onClick={onToggleDebug}>Datový přehled</DropdownItem>
                <DropdownItem toggle={false} onClick={logout} disabled={loggingOut}>
                    {loggingOut ? 'Odhlašování…' : 'Odhlásit se'}
                </DropdownItem>
                {logoutFailed && <div role="alert" className="text-danger px-3 py-2">Odhlášení se nezdařilo. Zkuste to znovu.</div>}
            </DropdownMenu>
        </Dropdown>
    );
};
